import { useState, useCallback } from 'react';
import api from '../services/api';

/**
 * Hook for managing database configurations
 */
export function useDatabases() {
  const [databases, setDatabases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * List all database configurations
   */
  const list = useCallback(async (isActive = true) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get('/database-configs', {
        params: { isActive },
      });
      setDatabases(data.data || []);
      return data.data;
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(message);
      console.error('Failed to list databases:', message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a new database configuration
   */
  const create = useCallback(async (dbData) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.post('/database-configs', dbData);
      
      // Add to list
      setDatabases((prev) => [data.data, ...prev]);
      return data.data;
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get a specific database configuration
   */
  const getById = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get(`/database-configs/${id}`);
      return data.data;
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update a database configuration
   */
  const update = useCallback(async (id, updates) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.put(`/database-configs/${id}`, updates);
      
      // Update in list
      setDatabases((prev) =>
        prev.map((db) => (db.id === id ? data.data : db))
      );
      return data.data;
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Delete a database configuration
   */
  const remove = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      await api.delete(`/database-configs/${id}`);
      
      // Remove from list
      setDatabases((prev) => prev.filter((db) => db.id !== id));
      return true;
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Test connection to a database
   */
  const testConnection = useCallback(async (dbData) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.post('/database-configs/test-connection', {
        dbName: dbData.dbName,
        dbUser: dbData.dbUser,
        dbPassword: dbData.dbPassword,
      });
      return data;
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    databases,
    loading,
    error,
    list,
    create,
    getById,
    update,
    remove,
    testConnection,
  };
}
